import { FC, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { twMerge } from 'tailwind-merge';

import { AddAccount } from '@/features/account-actions';

import { totalBalanceQuery } from '@/entities/currency-account';
import {
  useAccountStore,
  useTotalBalanceStore,
  useVabsAccountsStore,
  useWidgetAccountStore,
} from '@/entities/currency-account/model/store';
import { StatementWidget } from '@/entities/statement';
import { WidgetSchema, WidgetType } from '@/entities/widget';

import { ROUTES } from '@/shared/lib';
import { Desktop, Link, Mobile } from '@/shared/ui';

import { isWidgetSchemaAccount } from '../lib/helpers';
import { useCustomizationStore } from '../model/store';
import { SlideCard } from './SlideCard';
import { AccountWidget } from './accounts-widget';
import { AssetsWidget } from './assets/assets-widget';
import { CardsWidget } from './cards/cards-widget';
import { MobileAccountActions } from './mobile-account-actions';
import { ProcessTransactionsWidget } from './process-transactions/process-transactions-widget';
import { FavoritesWidget } from './process-transactions/favorites-widget';
import { WorldTimeWidget } from './world-time/world-time-widget';
import { ExchangeCalculator } from '@/widgets/ExchangeCalculator';

interface WidgetProps {
  widget: WidgetSchema;
}

const AccountMain = ({ widget }: WidgetProps) => {
  const [isAddAccountOpen, setIsAddAccountOpen] = useState(false);
  const account = useAccountStore((state) => state.account);
  const totalAccount = useAccountStore((state) => state.totalAccount);
  const setTotalAccount = useAccountStore((state) => state.setTotalAccount);
  const setBalance = useTotalBalanceStore((state) => state.setBalance);
  const accounts = useVabsAccountsStore((state) => state.accounts);

  const totalAcc = useVabsAccountsStore((state) => state.totalBalanceAcc);

  const { data: totalBalance } = useQuery({
    enabled: Boolean(account?.virtualId),
    queryKey: ['total-balance', totalAcc],
    queryFn: () =>
      totalBalanceQuery({
        virtualAccountId: totalAcc?.virtualId ?? '',
        currencyId: totalAcc?.currencyId ?? '',
      }),
  });

  useEffect(() => {
    if (totalBalance) {
      setBalance({ amount: totalBalance.toString(), symbol: '$' });
    }
  }, [totalBalance]);

  useEffect(() => {
    //update balance if acc is not 'all accounts'
    if (totalAccount) {
      const newAcc = accounts.find((i) => i.id === totalAccount.id);
      const val = totalAccount.value;
      if (!!newAcc && newAcc?.value !== val) {
        setTotalAccount(newAcc);
      }
    }
  }, [accounts, totalAccount]);

  const isTotal = totalAccount?.id === 'all';

  if (isWidgetSchemaAccount(widget)) {
    return (
      <>
        <Desktop>
          {isTotal && (
            <div
              className={twMerge(
                'relative w-full cursor-default !text-base-500 hover:!text-base-500 active:!text-base-500',
              )}
            >
              <SlideCard className="p-6" size="lg">
                <CardsWidget
                  isTotal
                  account={totalAccount}
                  totalBalance={totalBalance}
                  setAccount={setTotalAccount}
                  onAddAccount={() => {
                    setIsAddAccountOpen(true);
                  }}
                />
              </SlideCard>
            </div>
          )}
          {!isTotal && (
            <Link
              to={`${ROUTES.ACCOUNT}?id=${totalAccount?.id}`}
              className={twMerge(
                'relative w-full !text-base-500 hover:!text-base-500 active:!text-base-500',
              )}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <SlideCard className="p-6" size="lg">
                <CardsWidget
                  isTotal
                  account={totalAccount}
                  totalBalance={totalBalance}
                  setAccount={setTotalAccount}
                  onAddAccount={() => {
                    setIsAddAccountOpen(true);
                  }}
                />
              </SlideCard>
            </Link>
          )}
          <AddAccount
            open={isAddAccountOpen}
            onOpenChange={setIsAddAccountOpen}
          >
            <></>
          </AddAccount>
        </Desktop>
        <Mobile>
          {isTotal && (
            <div
              className={twMerge(
                'relative h-[354px] w-full cursor-default !text-base-500 hover:!text-base-500 active:!text-base-500',
              )}
            >
              <SlideCard className="h-[284px] md:h-[354px]">
                <AccountWidget
                  isTotal
                  account={totalAccount}
                  //totalBalance={totalBalance}
                  setAccount={setTotalAccount}
                  onAddAccount={() => {
                    setIsAddAccountOpen(true);
                  }}
                />
              </SlideCard>
            </div>
          )}
          {!isTotal && (
            <Link
              to={`${ROUTES.ACCOUNT}?id=${totalAccount?.id}`}
              className={twMerge(
                'relative w-full !text-base-500 hover:!text-base-500 active:!text-base-500',
              )}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <SlideCard className="h-[284px] md:h-[354px]">
                <AccountWidget
                  isTotal
                  account={totalAccount}
                  //totalBalance={totalBalance}
                  setAccount={setTotalAccount}
                  onAddAccount={() => {
                    setIsAddAccountOpen(true);
                  }}
                />
              </SlideCard>
            </Link>
          )}

          <MobileAccountActions account={isTotal ? account : totalAccount} />
          <AddAccount
            open={isAddAccountOpen}
            onOpenChange={setIsAddAccountOpen}
          >
            <></>
          </AddAccount>
        </Mobile>
      </>
    );
  }
  return null;
};

const WorldTime = () => {
  return (
    <SlideCard>
      <WorldTimeWidget />
    </SlideCard>
  );
};

const Account = ({ widget }: WidgetProps) => {
  const [isAddAccountOpen, setIsAddAccountOpen] = useState(false);
  const account = useWidgetAccountStore((state) => state.account);
  const setAccount = useWidgetAccountStore((state) => state.setAccount);
  const accounts = useVabsAccountsStore((state) => state.accounts);
  const widgets = useCustomizationStore((state) => state.accountsBgs);

  useEffect(() => {
    const activeAccounts = accounts.filter((i) => i.status === 'ACTIVE');
    if (activeAccounts.length && !account) {
      setAccount(activeAccounts[0]);
    }
    if (account) {
      const newAcc = accounts.find((i) => i.id === account.id);
      const val = account.value;
      if (!!newAcc && newAcc?.value !== val) {
        setAccount(newAcc);
      }
    }
  }, [accounts, account]);

  if (isWidgetSchemaAccount(widget)) {
    return (
      <>
        <Link
          className="relative w-full !text-base-500 hover:!text-base-500 active:!text-base-500"
          to={`${ROUTES.ACCOUNT}?id=${account?.id}`}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <SlideCard className="h-[284px] md:h-[354px]">
            <AccountWidget
              account={account}
              setAccount={setAccount}
              totalBalance={account?.value ?? 0}
              onAddAccount={() => {
                setIsAddAccountOpen(true);
              }}
            />
          </SlideCard>
        </Link>
        <MobileAccountActions account={account} />
        <AddAccount open={isAddAccountOpen} onOpenChange={setIsAddAccountOpen}>
          <></>
        </AddAccount>
      </>
    );
  }
};

const Transactions = () => {
  return (
    <SlideCard>
      <ProcessTransactionsWidget />
    </SlideCard>
  );
};

const Favorites = () => {
  return (
    <SlideCard>
      <FavoritesWidget />
    </SlideCard>
  );
};

const Assets = () => {
  return (
    <SlideCard className="p-0">
      <AssetsWidget />
    </SlideCard>
  );
};

const Statements = () => {
  return (
    <SlideCard className="p-0">
      <StatementWidget />
    </SlideCard>
  );
};

const Exchange = () => {
  return <ExchangeCalculator isWidget />
}

export const WIDGETS_MAP: Record<WidgetType, FC<WidgetProps> | null> = {
  'account-main': AccountMain,
  'world-time': WorldTime,
  account: Account,
  transactions: Transactions,
  favorites: Favorites,
  assets: Assets,
  statements: Statements,
  exchange: Exchange,
  cards: null,
  news: null,
};
